/* eslint-disable react/jsx-no-undef */
/* eslint-disable no-nested-ternary */
/* eslint-disable react/destructuring-assignment */
import {
  Button,
  Grid,
  Group,
  Input,
  MultiSelect,
  Paper,
  Select,
  SimpleGrid,
  Space,
  TextInput,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import {
  IconBuilding,
  IconCalendar,
  IconClearAll,
  IconFileDots,
  IconQrcode,
  IconSearch,
} from '@tabler/icons-react';
import { getPageFilterInitialValues } from '../../../business/events/filter';
import { Audiencia, AudienciaCodigo, Fonte, Tipo, TipoCodigo } from '../../../business/events/general';
import useCurrentUser from '../../../hooks/useCurrentUser';
import { CompanyType, DocumentType, EventStatusType } from '../../../models/core/cache.type';
import { UserType } from '../../../models/core/users.type';
import { transformComboBoxGroupDataFromV5toV7 } from '../../../utils/mantine.utils';
import ContractPicker from '../ContractPicker/ContractPicker';
import EntityPicker from '../EntityPicker/EntityPicker';
import ProposalPicker from '../ProposalPicker/ProposalPicker';

type FilterData = {
  idEmpresa: string | null;
  listaAudiencia: string[];
  listaTipo: string[];
  listaStatus: string[];
  listaResponsavel: string[];
  fonte: string | undefined;
  idContrato: number | undefined;
  idEntidade: number | undefined;
  idProposta: number | undefined;
  periodo: Date[];
  idDocumentoTipo: string | null;
  numeroDocumento: string | null;
};

type FilterProps = {
  eventStatusData: EventStatusType[];
  companies: CompanyType[];
  users: UserType[];
  documents: DocumentType[];
  filter(data: FilterData): void;
  clear(): void;
  loading: boolean;
  filterData: {
    idContrato: number | undefined;
    idEntidade: number | undefined;
    idProposta: number | undefined;
    periodo: Date[];
    listaAudiencia: string[];
    listaTipo: string[];
    listaStatus: string[];
  } | null;
  zIndex: number;
};

function EventSearchFilter(props: FilterProps) {
  const [currentUser] = useCurrentUser();
  const pageFilter = '';
  const pageFilterInitialValues = getPageFilterInitialValues(pageFilter);

  const form = useForm<FilterData>({
    initialValues: {
      idEmpresa: currentUser.executivo ? null : currentUser.idEmpresa.toString(),
      listaResponsavel: [],
      fonte: props.filterData?.idContrato
        ? Fonte.Contrato
        : props.filterData?.idEntidade
        ? Fonte.Entidade
        : props.filterData?.idProposta
        ? Fonte.Entidade
        : undefined,
      idContrato: props.filterData?.idContrato,
      idEntidade: props.filterData?.idEntidade,
      idProposta: props.filterData?.idProposta,
      idDocumentoTipo: null,
      numeroDocumento: null,
      ...pageFilterInitialValues,
      periodo: props.filterData?.periodo ?? pageFilterInitialValues.periodo,
      listaAudiencia: props.filterData?.listaAudiencia ?? pageFilterInitialValues.listaAudiencia ?? [],
      listaTipo: props.filterData?.listaTipo ?? pageFilterInitialValues.listaTipo ?? [],
      listaStatus: props.filterData?.listaStatus ?? pageFilterInitialValues.listaStatus ?? [],
    },
    validate: {
      idContrato: (value: number | undefined, values: FilterData) => {
        if (values.fonte === Fonte.Contrato && !value) {
          return 'Campo obrigatório';
        }
        return null;
      },
      idEntidade: (value: number | undefined, values: FilterData) => {
        if (values.fonte === Fonte.Entidade && !value) {
          return 'Campo obrigatório';
        }
        return null;
      },
      idProposta: (value: number | undefined, values: FilterData) => {
        if (values.fonte === Fonte.Proposta && !value) {
          return 'Campo obrigatório';
        }
        return null;
      },
      numeroDocumento: (value: string | null, values: FilterData) => {
        if (values.idDocumentoTipo && (!value || value.trim() === '')) {
          return 'Campo obrigatório';
        }
        return null;
      },
    },
  });

  const handleSubmit = async (values: FilterData) => {
    if (form.validate().hasErrors) {
      return;
    }

    const formItem: FilterData = {
      ...values,
    };

    if (formItem.fonte === Fonte.Contrato) {
      formItem.idEntidade = undefined;
      formItem.idProposta = undefined;
    } else if (formItem.fonte === Fonte.Entidade) {
      formItem.idContrato = undefined;
      formItem.idProposta = undefined;
    } else if (formItem.fonte === Fonte.Proposta) {
      formItem.idContrato = undefined;
      formItem.idEntidade = undefined;
    } else {
      formItem.idContrato = undefined;
      formItem.idEntidade = undefined;
      formItem.idProposta = undefined;
    }

    if (!formItem.fonte) {
      delete (formItem as any).fonte;
    }

    props.filter(formItem);
  };

  return (
    <form noValidate>
      <Paper shadow="xs" p="md" withBorder>
        <Grid columns={6}>
          <Grid.Col span={1}>
            <Select
              leftSection={<IconBuilding size={15} />}
              label="Empresa"
              description="Filtro: igual"
              placeholder="Todos..."
              data={props.companies.map((x) => {
                return {
                  value: x.idEmpresa.toString(),
                  label: x.empresa,
                };
              })}
              searchable
              clearable={currentUser.executivo}
              disabled={!currentUser.executivo}
              {...form.getInputProps('idEmpresa')}
              comboboxProps={{ zIndex: props.zIndex + 1 }}
            />
          </Grid.Col>
          <Grid.Col span={1}>
            <MultiSelect
              label="Responsável"
              description="Filtro: igual"
              placeholder="Todos..."
              data={transformComboBoxGroupDataFromV5toV7(
                props.users.map((x) => {
                  return {
                    label: x.idUsuario === currentUser.idUsuario ? `${x.nomeCompleto} (eu)` : x.nomeCompleto,
                    value: x.idUsuario,
                    group: x.empresa,
                  };
                })
              )}
              clearable
              searchable
              {...form.getInputProps('listaResponsavel')}
              comboboxProps={{ zIndex: props.zIndex + 1 }}
            />
          </Grid.Col>
          <Grid.Col span={2}>
            <div>
              <Input.Wrapper
                label="Data Inicial / Final"
                description="Filtro: entre"
                style={{ marginBottom: 5 }}
              >
                <div />
              </Input.Wrapper>
              <DatePickerInput
                type="range"
                placeholder="Período"
                leftSection={<IconCalendar size={15} />}
                clearable
                numberOfColumns={2}
                allowSingleDateInRange
                {...form.getInputProps('periodo')}
                popoverProps={{ zIndex: props.zIndex + 1 }}
              />
            </div>
          </Grid.Col>
          <Grid.Col span={1}>
            <Select
              label="Fonte"
              description="Filtro: -"
              placeholder="Todos..."
              data={[
                { label: Fonte.Contrato, value: Fonte.Contrato },
                { label: Fonte.Entidade, value: Fonte.Entidade },
                { label: Fonte.Proposta, value: Fonte.Proposta },
              ]}
              clearable
              {...form.getInputProps('fonte')}
              comboboxProps={{ zIndex: props.zIndex + 1 }}
            />
          </Grid.Col>
          {form.values.fonte === Fonte.Contrato && (
            <Grid.Col span={1}>
              <ContractPicker
                label="Contrato #"
                description="Filtro: igual"
                formItem={form.getInputProps('idContrato')}
                showProposal={false}
                idsToBeDisabled={[]}
                disabled={false}
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                callback={() => {}}
                zIndex={props.zIndex + 1}
                useCache
              />
            </Grid.Col>
          )}
          {form.values.fonte === Fonte.Entidade && (
            <Grid.Col span={1}>
              <EntityPicker
                label="Entidade #"
                description="Filtro: igual"
                formItem={form.getInputProps('idEntidade')}
                idsToBeDisabled={[]}
                disabled={false}
                required
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                callback={() => {}}
                zIndex={props.zIndex + 1}
                useCache
                allowMultiple={false}
                query={undefined}
              />
            </Grid.Col>
          )}
          {form.values.fonte === Fonte.Proposta && (
            <Grid.Col span={1}>
              <ProposalPicker
                label="Proposta #"
                description="Filtro: igual"
                formItem={form.getInputProps('idProposta')}
                idsToBeDisabled={[]}
                disabled={false}
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                callback={() => {}}
                zIndex={props.zIndex + 1}
                useCache
              />
            </Grid.Col>
          )}
        </Grid>
        <Space h="md" />

        <Grid columns={6}>
          <Grid.Col span={2}>
            <MultiSelect
              label="Audiência"
              description="Filtro: igual"
              placeholder="Todos..."
              data={[
                { label: Audiencia.Comercial, value: AudienciaCodigo.Comercial },
                { label: Audiencia.Financeiro, value: AudienciaCodigo.Financeiro },
                { label: Audiencia.Operacional, value: AudienciaCodigo.Operacional },
              ]}
              clearable
              searchable
              {...form.getInputProps('listaAudiencia')}
              disabled={!!pageFilter || (props.filterData?.listaAudiencia.length ?? 0) > 0}
              comboboxProps={{ zIndex: props.zIndex + 1 }}
            />
          </Grid.Col>
          <Grid.Col span={2}>
            <MultiSelect
              label="Tipo"
              description="Filtro: igual"
              placeholder="Todos..."
              data={[
                { label: Tipo.Cobranca, value: TipoCodigo.Cobranca },
                { label: Tipo.Coleta, value: TipoCodigo.Coleta },
                { label: Tipo.Comissao, value: TipoCodigo.Comissao },
                { label: Tipo.Documetacao, value: TipoCodigo.Documetacao },
                { label: Tipo.Expiracao, value: TipoCodigo.Expiracao },
                { label: Tipo.Fechamento, value: TipoCodigo.Fechamento },
                { label: Tipo.Lembrete, value: TipoCodigo.Lembrete },
                { label: Tipo.MobilizacaoEnviar, value: TipoCodigo.MobilizacaoEnviar },
                { label: Tipo.MobilizacaoRetirar, value: TipoCodigo.MobilizacaoRetirar },
              ]}
              clearable
              searchable
              {...form.getInputProps('listaTipo')}
              disabled={!!pageFilter || (props.filterData?.listaTipo.length ?? 0) > 0}
              comboboxProps={{ zIndex: props.zIndex + 1 }}
            />
          </Grid.Col>
          <Grid.Col span={2}>
            <MultiSelect
              label="Status"
              description="Filtro: igual"
              placeholder="Todos..."
              data={props.eventStatusData.map((x) => {
                return {
                  value: x.codigoEventoStatus,
                  label: x.eventoStatus,
                };
              })}
              clearable
              searchable
              {...form.getInputProps('listaStatus')}
              disabled={(props.filterData?.listaStatus.length ?? 0) > 0}
              comboboxProps={{ zIndex: props.zIndex + 1 }}
            />
          </Grid.Col>
        </Grid>
        <Space h="md" />

        <Grid columns={6}>
          <Grid.Col span={2}>
            <div>
              <Input.Wrapper label="Documento" description="Filtro: igual" style={{ marginBottom: 5 }}>
                <div />
              </Input.Wrapper>
              <Group>
                <Select
                  leftSection={<IconFileDots size={15} />}
                  placeholder="Todos..."
                  data={props.documents.map((x) => {
                    return {
                      value: x.idDocumentoTipo.toString(),
                      label: x.documentoTipo,
                    };
                  })}
                  searchable
                  clearable
                  {...form.getInputProps('idDocumentoTipo')}
                  comboboxProps={{ zIndex: props.zIndex + 1 }}
                />
                <TextInput
                  leftSection={<IconQrcode size={15} />}
                  placeholder="Número do documento"
                  maxLength={50}
                  {...form.getInputProps('numeroDocumento')}
                />
              </Group>
            </div>
          </Grid.Col>
        </Grid>
        <Space h="md" />

        <SimpleGrid cols={1}>
          <Group justify="space-between">
            <Group>
              <Button
                color="secondary"
                leftSection={<IconClearAll size={18} />}
                disabled={props.loading}
                onClick={() => {
                  form.reset();
                  form.setValues({
                    ...pageFilterInitialValues,
                    idEmpresa: currentUser.executivo ? undefined : form.values.idEmpresa,
                    listaResponsavel: undefined,
                    fonte: null as any,
                    idContrato: undefined,
                    idEntidade: undefined,
                    idProposta: undefined,
                    periodo: [],
                    idDocumentoTipo: null,
                    numeroDocumento: '',
                    listaStatus: [],
                    listaTipo: [],
                  });
                  props.clear();
                }}
              >
                Limpar
              </Button>
              <Button
                leftSection={<IconSearch size={18} />}
                loading={props.loading}
                onClick={() => {
                  handleSubmit(form.values);
                }}
              >
                Pesquisar
              </Button>
            </Group>
          </Group>
        </SimpleGrid>
      </Paper>
      <Space h="xl" />
    </form>
  );
}

export { EventSearchFilter };
export type { FilterData };
